/**
 * Yandex Auth Service
 * 
 * API service for managing Yandex Webmaster credentials
 */

import { apiClient } from './api'

export interface YandexCredentials {
  id: number
  user_id: number | null
  account_name: string
  yandex_login: string
  is_active: boolean
  is_session_valid: boolean
  crc_token_age_hours: number | null
  is_crc_token_valid: boolean | null
  last_auth_at: string | null
  last_error: string | null
  created_at: string
  updated_at: string
}

export interface YandexCredentialsList {
  items: YandexCredentials[]
  total: number
}

export interface BrowserSessionRequest {
  account_name: string
  yandex_login: string
  session_cookies: any[]
  crc?: string | null
  balancer_id?: string | null
  user_id?: number | null
}

export interface SessionFile {
  cookies: any[]
  crc?: string
  balancer_id?: string
  user_agent?: string
  url?: string
  timestamp?: number
}

export interface TestResult {
  valid: boolean
  message: string
  last_auth?: string
  expires_at?: string
}

/**
 * Get list of all Yandex credentials
 */
export async function getCredentialsList(
  skip: number = 0,
  limit: number = 100
): Promise<YandexCredentialsList> {
  const response = await apiClient.get('/yandex-auth', {
    params: { skip, limit }
  })
  return response.data
}

/**
 * Get single credentials by ID
 */
export async function getCredentials(id: number): Promise<YandexCredentials> {
  const response = await apiClient.get(`/yandex-auth/${id}`)
  return response.data
}

/**
 * Save browser session (from wordcraft_session.json)
 */
export async function saveBrowserSession(
  data: BrowserSessionRequest
): Promise<YandexCredentials> {
  const response = await apiClient.post('/yandex-auth/browser', data)
  return response.data
}

/**
 * Update credentials
 */
export async function updateCredentials(
  id: number,
  data: { account_name?: string; is_active?: boolean }
): Promise<YandexCredentials> {
  const response = await apiClient.patch(`/yandex-auth/${id}`, data)
  return response.data
}

/**
 * Delete credentials
 */
export async function deleteCredentials(id: number): Promise<void> {
  await apiClient.delete(`/yandex-auth/${id}`)
}

/**
 * Test credentials validity
 */
export async function testCredentials(id: number): Promise<TestResult> {
  const response = await apiClient.post(`/yandex-auth/${id}/test`)
  return response.data
}

/**
 * Parse wordcraft_session.json file
 */
export function parseSessionFile(fileContent: string): SessionFile {
  try {
    const data = JSON.parse(fileContent)
    
    // Validate required fields
    if (!data.cookies || !Array.isArray(data.cookies)) {
      throw new Error('Invalid session file: missing cookies array')
    }
    
    return data
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON file')
    }
    throw error
  }
}

/**
 * Extract Yandex login from cookies
 */
export function extractYandexLogin(cookies: any[]): string | null {
  // Try to find login in cookies
  const loginCookie = cookies.find(c => c.name === 'yandex_login')
  if (loginCookie && loginCookie.value) {
    return loginCookie.value
  }
  
  // Try to find in Session_id cookie (encoded)
  const sessionCookie = cookies.find(c => c.name === 'Session_id')
  if (sessionCookie) {
    // Session_id format: "3:timestamp.uid.sid:..."
    // We can't easily extract login from it, so return null
    return null
  }
  
  return null
}

